import { wsClient } from '../services/websocket';
import { webrtcManager } from '../services/webrtc';
import { isWebViewMode } from '../utils/webviewBridge';
import type { CallEndInfo, IncomingCallSnapshot } from './callTypes';
import type { CallStateAction } from './callStateReducer';
import { consumeCallReturnPath } from './callNavigationState';
import type { ActiveCall, IncomingCall } from '../store/callStore';

type RetryAnswerCleanupParams = {
  incomingCall: IncomingCallSnapshot;
  dispatchCallState: (action: CallStateAction) => void;
  playRingtone: () => void;
  stopRingtone: () => void;
  isLocalIncomingCallLeader: (callSessionId: string) => boolean;
  onFallbackFailure: () => void;
};

export function cleanupAfterFailedAnswer(params: RetryAnswerCleanupParams): void {
  try {
    webrtcManager.close();
  } catch {
    // ignore
  }

  try {
    params.dispatchCallState({ type: 'call/incoming-answer-failed', incomingCall: params.incomingCall });
    if (params.isLocalIncomingCallLeader(params.incomingCall.callSessionId)) {
      params.playRingtone();
    } else {
      params.stopRingtone();
    }
  } catch {
    params.onFallbackFailure();
  }
}

type EndedCallCleanupParams = {
  info?: CallEndInfo;
  activeCall: ActiveCall | null;
  incomingCall: IncomingCall | null;
  clearPendingCallAction: () => void;
  setLastCallEndInfo: (info: { callSessionId: string; reason: string; source: NonNullable<CallEndInfo['source']>; at: number }) => void;
  effects: {
    showEndedToast: (info?: CallEndInfo) => void;
    markCallEnded: (callSessionId: string, info?: {
      activeCall?: ActiveCall | null;
      incomingCall?: IncomingCall | null;
      reason?: string;
      source?: CallEndInfo['source'];
    }) => void;
    handlePostCallJoin: (callSessionId: string) => void;
  };
  navigate: (to: string) => void;
  endNativeAudioRuntimeCall: (callSessionId: string) => void;
  endCallState: () => void;
  stopRingtone: () => void;
  stopOutgoingDialTone: () => void;
};

export function cleanupAfterCallEnd(params: EndedCallCleanupParams): void {
  const useNativeFinalizedHistory =
    isWebViewMode
    && params.activeCall?.nativeRuntimeMode === 'video-native';
  params.clearPendingCallAction();

  if (params.activeCall?.callSessionId) {
    const endedCallSessionId = params.activeCall.callSessionId;
    wsClient.releaseCallLock(endedCallSessionId);
    params.setLastCallEndInfo({
      callSessionId: endedCallSessionId,
      reason: (params.info?.reason || 'unknown').toString(),
      source: params.info?.source || 'unknown',
      at: Date.now()
    });
    params.effects.showEndedToast(params.info);
    if (!useNativeFinalizedHistory) {
      params.effects.markCallEnded(endedCallSessionId, {
        activeCall: params.activeCall,
        incomingCall: params.incomingCall,
        reason: params.info?.reason,
        source: params.info?.source
      });
    }
    params.effects.handlePostCallJoin(endedCallSessionId);
  } else if (params.incomingCall?.callSessionId) {
    params.effects.markCallEnded(params.incomingCall.callSessionId, {
      incomingCall: params.incomingCall,
      reason: params.info?.reason,
      source: params.info?.source
    });
  }

  if (isWebViewMode && params.activeCall?.callSessionId) {
    params.endNativeAudioRuntimeCall(params.activeCall.callSessionId);
  } else {
    webrtcManager.close();
  }

  params.endCallState();
  params.stopRingtone();
  params.stopOutgoingDialTone();

  if (wsClient.isExternalMode()) {
    wsClient.leaveExternalMode();
    wsClient.connect();
  }

  if (window.location.pathname === '/call') {
    params.navigate(consumeCallReturnPath('/messages'));
  }
}
